import React, { useEffect, useState, useCallback, useRef } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import SendPushModal from "./components/SendPushModal";
import {
  getUserNotificationList,
  deleteUserNotification,
  APP_ROUTES,
} from "../api/userNotificationApi";
import {
  FiSend,
  FiTrash2,
  FiRefreshCw,
  FiSearch,
  FiUsers,
  FiUser,
  FiExternalLink,
  FiEye,
  FiRepeat,
  FiX,
  FiImage,
} from "react-icons/fi";

const UserNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    currentPage: 1,
    perPage: 10,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [mode, setMode] = useState("all"); // 'all' | 'bulk' | 'particular'
  const [userIdFilter, setUserIdFilter] = useState("");
  const debounceTimer = useRef(null);

  // Send Push Modal
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);
  const [pushModalProps, setPushModalProps] = useState({
    defaultUserId: null,
    defaultUsername: null,
    initialMode: "bulk",
    initialValues: null,
  });

  // Details Modal
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Lightbox
  const [previewImage, setPreviewImage] = useState(null);

  // Debounce search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getUserNotificationList({
        page,
        limit,
        search: debouncedSearch,
        mode,
        userId: userIdFilter,
      });

      if (res?.data && Array.isArray(res.data)) {
        setNotifications(res.data);
      } else if (Array.isArray(res)) {
        setNotifications(res);
      } else {
        setNotifications([]);
      }

      if (res?.pagination) {
        setPagination(res.pagination);
      } else if (typeof res?.total === "number") {
        setPagination({
          total: res.total,
          lastPage: Math.ceil(res.total / limit) || 1,
          currentPage: page,
          perPage: limit,
        });
      }
    } catch (err) {
      setError(err.message || "Failed to load notifications");
      toast.error(err.message || "Failed to fetch user notifications");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, mode, userIdFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleOpenNewPush = (targetMode = "bulk") => {
    setPushModalProps({
      defaultUserId: null,
      defaultUsername: null,
      initialMode: targetMode,
      initialValues: null,
    });
    setIsPushModalOpen(true);
  };

  const handleResend = (item) => {
    setPushModalProps({
      defaultUserId: item.userId || null,
      defaultUsername: item.recipientUsername || null,
      initialMode: item.userId ? "particular" : "bulk",
      initialValues: {
        title: item.title,
        message: item.message,
        redirectRoute: item.redirectRoute || "/home",
        url: item.url || "",
        imageUrl: item.imageUrl || item.image || null,
      },
    });
    setIsPushModalOpen(true);
  };

  const handleOpenDelete = (item) => {
    setNotificationToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteUserNotification(notificationToDelete.id);
      toast.success("Notification deleted successfully");
      setDeleteModalOpen(false);
      setNotificationToDelete(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.message || "Failed to delete notification");
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setMode("all");
    setUserIdFilter("");
    setPage(1);
  };

  // Stats calculation
  const bulkCount = notifications.filter(
    (n) => !n.userId || n.type === "bulk" || n.audience?.toLowerCase().includes("all")
  ).length;
  const particularCount = notifications.filter(
    (n) => n.userId || n.audience?.toLowerCase().includes("particular")
  ).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const totalPages = pagination?.lastPage || Math.ceil((pagination?.total || 0) / limit) || 1;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#ffffcc] text-[#999900] flex items-center justify-center text-2xl shadow-xs">
                📢
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  User Push Notifications
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  FCM Push Notification module & broadcast dispatch ledger
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                onClick={fetchNotifications}
                disabled={loading}
                className="p-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-gray-600 transition shadow-xs cursor-pointer disabled:opacity-50"
                title="Refresh notification history"
              >
                <FiRefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>

              <button
                onClick={() => handleOpenNewPush("bulk")}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black font-bold text-sm rounded-xl shadow-xs transition cursor-pointer"
              >
                <FiSend size={15} />
                <span>Send Push Notification</span>
              </button>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Total Sent
              </p>
              <p className="text-2xl font-black text-gray-900 mt-1">
                {pagination?.total || notifications.length}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">Dispatched records</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Bulk Broadcasts
              </p>
              <p className="text-2xl font-black text-blue-600 mt-1">
                {bulkCount}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">Broadcast to all users</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Particular Users
              </p>
              <p className="text-2xl font-black text-amber-600 mt-1">
                {particularCount}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">Targeted single deliveries</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Current Page
              </p>
              <p className="text-2xl font-black text-gray-900 mt-1">
                {page} <span className="text-sm font-normal text-gray-400">/ {totalPages}</span>
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {notifications.length} on page
              </p>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-xs space-y-3">
            <div className="flex flex-col md:flex-row items-center gap-3">
              {/* Search Bar */}
              <div className="relative flex-1 w-full">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search by title, message, username, or email..."
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setDebouncedSearch("");
                      setPage(1);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={15} />
                  </button>
                )}
              </div>

              {/* Mode Filter Buttons */}
              <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl border border-gray-200 w-full md:w-auto">
                <button
                  onClick={() => { setMode("all"); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    mode === "all" ? "bg-[#ffff00] text-black shadow-xs" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => { setMode("bulk"); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    mode === "bulk" ? "bg-[#ffff00] text-black shadow-xs" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  📢 Bulk
                </button>
                <button
                  onClick={() => { setMode("particular"); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    mode === "particular" ? "bg-[#ffff00] text-black shadow-xs" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  👤 Particular
                </button>
              </div>

              {/* Specific User ID Filter */}
              <div className="w-full md:w-44">
                <input
                  type="number"
                  placeholder="Filter User ID..."
                  value={userIdFilter}
                  onChange={(e) => {
                    setUserIdFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-3 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] focus:bg-white transition"
                />
              </div>

              {(search || mode !== "all" || userIdFilter) && (
                <button
                  onClick={resetFilters}
                  className="px-3.5 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition whitespace-nowrap"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Table Card */}
          <div className="bg-white rounded-2xl shadow-xs border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-gray-800">
                  Dispatched Push History
                </h2>
                <p className="text-[11px] text-gray-400">
                  Persisted records from <span className="font-mono text-gray-600">user_notification</span>
                </p>
              </div>
              <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-lg">
                Total: {pagination?.total || notifications.length}
              </span>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400 text-sm">
                <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                <span>Loading push notifications...</span>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <p className="text-sm text-red-600 font-medium mb-2">{error}</p>
                <button
                  onClick={fetchNotifications}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-xs font-semibold rounded-lg transition"
                >
                  Try Again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-20 text-center space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto text-2xl">
                  📭
                </div>
                <h3 className="text-base font-bold text-gray-800">
                  No Push Notifications Found
                </h3>
                <p className="text-xs text-gray-400 max-w-sm mx-auto">
                  {search || mode !== "all" || userIdFilter
                    ? "Try adjusting your filters or search keywords."
                    : "No notifications have been dispatched yet. Click below to send your first broadcast!"}
                </p>
                <button
                  onClick={() => handleOpenNewPush("bulk")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] text-black text-xs font-bold rounded-xl shadow-xs transition"
                >
                  <FiSend size={14} /> Send Broadcast Now
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/80 text-left text-[11px] text-gray-400 uppercase tracking-wider border-b border-gray-100">
                      <th className="px-5 py-3.5 w-12 text-center">#</th>
                      <th className="px-4 py-3.5 w-16">Banner</th>
                      <th className="px-5 py-3.5">Content</th>
                      <th className="px-5 py-3.5">Audience</th>
                      <th className="px-5 py-3.5 hidden md:table-cell">Action Route</th>
                      <th className="px-5 py-3.5 hidden lg:table-cell">Date & Time</th>
                      <th className="px-5 py-3.5 text-center w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {notifications.map((item, idx) => {
                      const isBulk =
                        !item.userId ||
                        item.type === "bulk" ||
                        item.audience?.toLowerCase().includes("all");
                      const bannerSrc = item.imageUrl || item.image || null;
                      const routeInfo = APP_ROUTES.find(
                        (r) => r.value === item.redirectRoute
                      );

                      return (
                        <tr key={item.id || idx} className="hover:bg-gray-50/70 transition">
                          {/* Index */}
                          <td className="px-5 py-4 text-center font-mono text-xs text-gray-400">
                            {(page - 1) * limit + idx + 1}
                          </td>

                          {/* Thumbnail */}
                          <td className="px-4 py-4">
                            {bannerSrc ? (
                              <button
                                type="button"
                                onClick={() => setPreviewImage(bannerSrc)}
                                className="group relative block w-11 h-11 rounded-xl overflow-hidden border border-gray-200 focus:outline-none"
                                title="Click to view full image"
                              >
                                <img
                                  src={bannerSrc}
                                  alt={item.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition duration-200"
                                />
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                                  <FiEye size={12} className="text-white" />
                                </div>
                              </button>
                            ) : (
                              <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-gray-300">
                                <FiImage size={16} />
                              </div>
                            )}
                          </td>

                          {/* Content */}
                          <td className="px-5 py-4">
                            <div className="max-w-md">
                              <p className="font-bold text-gray-900 line-clamp-1">
                                {item.title}
                              </p>
                              <p className="text-xs text-gray-500 line-clamp-2 mt-0.5 leading-relaxed">
                                {item.message}
                              </p>
                              {item.url && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mt-1 font-mono"
                                >
                                  <FiExternalLink size={11} /> {item.url}
                                </a>
                              )}
                            </div>
                          </td>

                          {/* Audience */}
                          <td className="px-5 py-4 whitespace-nowrap">
                            {isBulk ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200/60">
                                <FiUsers size={12} />
                                <span>All Users (Bulk)</span>
                              </span>
                            ) : (
                              <div className="space-y-0.5">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200/60">
                                  <FiUser size={12} />
                                  <span>User #{item.userId}</span>
                                </span>
                                {(item.recipientUsername || item.recipientEmail) && (
                                  <p className="text-[11px] text-gray-500 font-mono">
                                    <Link
                                      to={`/user/${item.userId}`}
                                      className="hover:underline text-blue-600"
                                    >
                                      @{item.recipientUsername || item.recipientEmail}
                                    </Link>
                                  </p>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Action Route */}
                          <td className="px-5 py-4 hidden md:table-cell whitespace-nowrap">
                            {item.redirectRoute ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-mono bg-gray-100 text-gray-700">
                                <span>{routeInfo?.icon || "📱"}</span>
                                <span>{item.redirectRoute}</span>
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>

                          {/* Date */}
                          <td className="px-5 py-4 hidden lg:table-cell whitespace-nowrap">
                            <p className="text-xs font-medium text-gray-700">
                              {formatDate(item.createdAt)}
                            </p>
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1">
                              {/* View Details */}
                              <button
                                onClick={() => setSelectedNotification(item)}
                                className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition cursor-pointer"
                                title="View details"
                              >
                                <FiEye size={15} />
                              </button>

                              {/* Resend / Duplicate */}
                              <button
                                onClick={() => handleResend(item)}
                                className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition cursor-pointer"
                                title="Duplicate / Resend"
                              >
                                <FiRepeat size={15} />
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => handleOpenDelete(item)}
                                className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition cursor-pointer"
                                title="Delete record"
                              >
                                <FiTrash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
                <span>
                  Showing Page <strong className="text-gray-800">{page}</strong> of{" "}
                  <strong className="text-gray-800">{totalPages}</strong> (
                  {pagination?.total || notifications.length} total notifications)
                </span>

                <div className="flex items-center gap-1">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1.5 rounded-lg border font-bold transition ${
                          page === pageNum
                            ? "bg-[#ffff00] text-black border-[#ffff00] shadow-2xs"
                            : "bg-white text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-3 py-1.5 rounded-lg border bg-white hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Push Modal */}
      <SendPushModal
        isOpen={isPushModalOpen}
        onClose={() => setIsPushModalOpen(false)}
        defaultUserId={pushModalProps.defaultUserId}
        defaultUsername={pushModalProps.defaultUsername}
        initialMode={pushModalProps.initialMode}
        initialValues={pushModalProps.initialValues}
        onSuccess={() => {
          fetchNotifications();
        }}
      />

      {/* Detail Inspection Modal */}
      {selectedNotification && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setSelectedNotification(null)}
        >
          <div
            className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#ffffcc] text-[#999900] flex items-center justify-center font-bold">
                  🔔
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">
                    Notification #{selectedNotification.id}
                  </h3>
                  <p className="text-[11px] text-gray-400">
                    {formatDate(selectedNotification.createdAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {(selectedNotification.imageUrl || selectedNotification.image) && (
                <div className="rounded-xl overflow-hidden border border-gray-100 max-h-52">
                  <img
                    src={selectedNotification.imageUrl || selectedNotification.image}
                    alt={selectedNotification.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Title
                </p>
                <p className="text-base font-bold text-gray-900 mt-0.5">
                  {selectedNotification.title}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                  Message
                </p>
                <p className="text-xs text-gray-700 bg-gray-50 p-3 rounded-xl mt-0.5 leading-relaxed">
                  {selectedNotification.message}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[11px] font-bold text-gray-400 uppercase">Target Audience</p>
                  <p className="font-bold text-gray-800 mt-1">
                    {selectedNotification.userId ? `User #${selectedNotification.userId}` : "All Users (Bulk)"}
                  </p>
                  {selectedNotification.recipientUsername && (
                    <p className="text-[11px] text-gray-500 font-mono">
                      @{selectedNotification.recipientUsername}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[11px] font-bold text-gray-400 uppercase">Screen Route</p>
                  <p className="font-mono font-bold text-gray-800 mt-1">
                    {selectedNotification.redirectRoute || "/home"}
                  </p>
                </div>
              </div>

              {selectedNotification.url && (
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    External URL
                  </p>
                  <a
                    href={selectedNotification.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline font-mono break-all inline-flex items-center gap-1 mt-0.5"
                  >
                    <FiExternalLink size={12} /> {selectedNotification.url}
                  </a>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  const current = selectedNotification;
                  setSelectedNotification(null);
                  handleResend(current);
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-[#ffff00] hover:bg-[#e6e600] text-black rounded-xl transition"
              >
                <FiRepeat size={13} /> Re-send / Edit
              </button>
              <button
                onClick={() => setSelectedNotification(null)}
                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && notificationToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !deleteLoading && setDeleteModalOpen(false)}
        >
          <div
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
                <FiTrash2 size={24} />
              </div>
              <h3 className="text-base font-bold text-gray-800">
                Delete Push Notification Record?
              </h3>
              <p className="text-xs text-gray-500 mt-1 mb-4">
                Are you sure you want to delete notification #{notificationToDelete.id}? This will remove it from the sent history ledger.
              </p>

              <div className="bg-gray-50 rounded-xl p-3 text-left text-xs mb-5">
                <p className="font-bold text-gray-800 truncate">
                  {notificationToDelete.title}
                </p>
                <p className="text-gray-500 line-clamp-1 mt-0.5">
                  {notificationToDelete.message}
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={() => setDeleteModalOpen(false)}
                  className="w-full py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteLoading}
                  onClick={handleConfirmDelete}
                  className="w-full py-2.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow transition disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={13} /> Yes, Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-3 right-3 bg-black/60 text-white rounded-full p-2 hover:bg-black/90 transition z-10"
              title="Close image"
            >
              <FiX size={18} />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserNotifications;
